/**
 * MVP 1 shell + MVP 5 PT dashboard / admin link tools.
 */

import { useEffect, useState } from 'react';
import { BrowserRouter, Link, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { LoginScreen } from './auth/LoginScreen';
import { ApiError } from './api/client';
import { getMe, type Me } from './api/me';
import {
  acceptInvite,
  createPtLink,
  getClientsPt,
  getMembers,
  getPtClients,
  inviteUser,
  listPrograms,
  putProgram,
  type MemberRow,
} from './api/pt-links';
import { getDiary, getWorkouts } from './api/client-reads';
import { getBuildEnv } from './env';
import { AdminConsole } from './AdminConsole';
import { postBillingCheckoutSession } from './api/billing';
import styles from './AppV1.module.css';

type Tab = 'home' | 'pt' | 'admin';

function BillingCard(): JSX.Element {
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void postBillingCheckoutSession()
      .then((r) => {
        if (cancelled) return;
        if (r.status === 'not_enabled') {
          setMsg('Checkout: not enabled (set flag f_billing_checkout + Stripe Prices to proceed).');
        } else {
          setMsg(typeof r.error === 'string' ? r.error : 'Checkout response received.');
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return (
    <section className={styles.subCard}>
      <h2 className={styles.subTitle}>Billing</h2>
      <p className={styles.muted} role="status">
        Stripe sandbox / test mode only. No live enforcement — see docs/DEFERRED-STRIPE-PRODUCTION.md.
      </p>
      {msg ? <p className={styles.muted}>{msg}</p> : null}
      {err ? (
        <p role="alert" className={styles.error}>
          {err}
        </p>
      ) : null}
    </section>
  );
}

function TenantPickerStub(props: { tenantId: string; role: string }): JSX.Element | null {
  const { tenantId, role } = props;
  if (role !== 'tenant_admin' && role !== 'admin') {
    return null;
  }
  return (
    <section className={styles.subCard}>
      <h2 className={styles.subTitle}>Tenant (stub)</h2>
      <p className={styles.muted}>
        Active context:{' '}
        <code className={styles.code}>{tenantId}</code>. Real tenant switching is deferred; pool signup:{' '}
        <code className={styles.code}>POST /tenants/signup</code> when flags allow.
      </p>
      <label className={styles.label} htmlFor="tenant-stub-select">
        Tenant picker
      </label>
      <select id="tenant-stub-select" className={styles.input} disabled value={tenantId}>
        <option value={tenantId}>{tenantId} (current)</option>
      </select>
    </section>
  );
}

function BuildInfoCard(): JSX.Element {
  const { envName, commitSha, buildTimestamp } = getBuildEnv();
  return (
    <section className={styles.subCard}>
      <h2 className={styles.subTitle}>build</h2>
      <dl className={styles.rows}>
        <dt className={styles.label}>env</dt>
        <dd className={styles.value}>{envName}</dd>
        <dt className={styles.label}>commit</dt>
        <dd className={styles.value}>{commitSha}</dd>
        <dt className={styles.label}>built at</dt>
        <dd className={styles.value}>{buildTimestamp}</dd>
      </dl>
    </section>
  );
}

function Nav(props: {
  tab: Tab;
  setTab: (t: Tab) => void;
  me: Me | null;
}): JSX.Element {
  const { tab, setTab, me } = props;
  const r = me?.role;
  const showPt =
    r === 'pt' || r === 'admin' || r === 'tenant_admin';
  const showAdminConsole = r === 'admin';
  const showAdmin = r === 'admin' || r === 'tenant_admin';
  return (
    <nav className={styles.nav} aria-label="Main">
      <button
        type="button"
        className={`${styles.navLink} ${tab === 'home' ? styles.navActive : ''}`}
        onClick={() => setTab('home')}
      >
        Home
      </button>
      {showPt ? (
        <button
          type="button"
          className={`${styles.navLink} ${tab === 'pt' ? styles.navActive : ''}`}
          onClick={() => setTab('pt')}
        >
          PT — My clients
        </button>
      ) : null}
      {showAdminConsole ? (
        <Link className={styles.navLink} to="/admin">
          Admin console
        </Link>
      ) : null}
      {showAdmin ? (
        <button
          type="button"
          className={`${styles.navLink} ${tab === 'admin' ? styles.navActive : ''}`}
          onClick={() => setTab('admin')}
        >
          Admin — links
        </button>
      ) : null}
    </nav>
  );
}

function PtPanel(props: { uid: string; role: string }): JSX.Element {
  const { uid, role } = props;
  const [clients, setClients] = useState<MemberRow[]>([]);
  const [trainers, setTrainers] = useState<MemberRow[]>([]);
  const [programs, setPrograms] = useState<string>('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteResult, setInviteResult] = useState<string | null>(null);
  const [progId, setProgId] = useState('demo-program-1');
  const [assignTo, setAssignTo] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [delegatePreview, setDelegatePreview] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setErr(null);
    try {
      if (role !== 'pt') {
        setClients([]);
        setTrainers([]);
        setPrograms(
          'PT roster and programs load only for accounts in the pt Cognito group. Admins can use the Admin — links tab to connect PTs and clients.',
        );
        return;
      }
      const [c, p] = await Promise.all([getPtClients(), listPrograms()]);
      setClients(c.clients);
      if (c.clients.length && !selectedClientId) {
        setSelectedClientId(c.clients[0]?.userId ?? '');
      }
      setTrainers([]);
      setPrograms(
        p.programs?.length
          ? JSON.stringify(p.programs, null, 2)
          : '(no programs yet — create one below)',
      );
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className={styles.subCard}>
      <h2 className={styles.subTitle}>PT workspace</h2>
      <p className={styles.muted}>
        Signed in as <code className={styles.code}>{uid}</code>. Invite flow reuses{' '}
        <code className={styles.code}>POST /invitations</code> (AdminCreateUser + temporary password + new-password
        challenge in the login screen).
      </p>

      <h3 className={styles.subTitle}>Invite client</h3>
      <form
        className={styles.form}
        onSubmit={async (e) => {
          e.preventDefault();
          setInviteResult(null);
          setErr(null);
          try {
            const r = await inviteUser({
              email: inviteEmail.trim(),
              role: 'client',
            });
            setInviteResult(
              `Invitation created. Share login email + temporaryPassword with the client, then they open this app and rotate password. Code: ${r.inviteCode ?? r.inviteToken}`,
            );
          } catch (er) {
            setErr(er instanceof ApiError ? `${er.status}: ${er.message}` : String(er));
          }
        }}
      >
        <input
          className={styles.input}
          type="email"
          placeholder="client@example.com"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          required
        />
        <button type="submit" className={styles.secondaryButton}>
          Send invite (DEV)
        </button>
      </form>
      {inviteResult ? <p className={styles.muted}>{inviteResult}</p> : null}

      <h3 className={styles.subTitle}>Delegated read (diary / workouts)</h3>
      <p className={styles.muted}>
        PT and admin roles can call <code className={styles.code}>GET /diary</code> and{' '}
        <code className={styles.code}>GET /workouts</code> with{' '}
        <code className={styles.code}>?forUser=&lt;clientSub&gt;</code> when linked + flags allow. Lists are empty until
        importer / write path materializes rows.
      </p>
      {clients.length === 0 ? null : (
        <div className={styles.form}>
          <label className={styles.label} htmlFor="cliPick">
            Client (cognito sub)
          </label>
          <select
            id="cliPick"
            className={styles.input}
            value={selectedClientId}
            onChange={(e) => {
              setSelectedClientId(e.target.value);
              setDelegatePreview(null);
            }}
          >
            {clients.map((c) => (
              <option key={c.userId} value={c.userId}>
                {c.email ?? c.userId}
              </option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => {
                void (async () => {
                  if (!selectedClientId) return;
                  setErr(null);
                  setDelegatePreview(null);
                  try {
                    const d = await getDiary({ forUser: selectedClientId });
                    setDelegatePreview(
                      `diary for ${d.userId} (${d.diary?.length ?? 0} rows)\n${JSON.stringify(d.diary?.slice(0, 5) ?? [], null, 2)}${(d.diary?.length ?? 0) > 5 ? '\n…' : ''}`,
                    );
                  } catch (er) {
                    setErr(er instanceof ApiError ? `${er.status}: ${er.message}` : String(er));
                  }
                })();
              }}
            >
              Load client diary
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => {
                void (async () => {
                  if (!selectedClientId) return;
                  setErr(null);
                  setDelegatePreview(null);
                  try {
                    const w = await getWorkouts({ forUser: selectedClientId });
                    setDelegatePreview(
                      `workouts for ${w.userId} (${w.workouts?.length ?? 0} rows)\n${JSON.stringify(w.workouts?.slice(0, 5) ?? [], null, 2)}${(w.workouts?.length ?? 0) > 5 ? '\n…' : ''}`,
                    );
                  } catch (er) {
                    setErr(er instanceof ApiError ? `${er.status}: ${er.message}` : String(er));
                  }
                })();
              }}
            >
              Load client workouts
            </button>
          </div>
          {delegatePreview ? (
            <pre className={styles.code} style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
              {delegatePreview}
            </pre>
          ) : null}
        </div>
      )}

      <h3 className={styles.subTitle}>My clients</h3>
      {clients.length === 0 ? (
        <p className={styles.muted}>No PT_CLIENT links yet — ask an admin to link you in the Admin tab.</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>userId</th>
              <th>email</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.userId}>
                <td className={styles.value}>{c.userId}</td>
                <td>{c.email ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3 className={styles.subTitle}>My PT (client view)</h3>
      {trainers.length === 0 ? (
        <p className={styles.muted}>No linked trainers — links appear after an admin connects you.</p>
      ) : (
        <ul className={styles.rows}>
          {trainers.map((t) => (
            <li key={t.userId} className={styles.value}>
              {t.email ?? t.userId}
            </li>
          ))}
        </ul>
      )}

      <h3 className={styles.subTitle}>Programs (stub)</h3>
      <pre className={styles.code} style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
        {programs || '—'}
      </pre>
      <form
        className={styles.form}
        onSubmit={async (e) => {
          e.preventDefault();
          setErr(null);
          try {
            await putProgram(progId.trim(), {
              assignedToUserIds: assignTo
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            });
            await load();
          } catch (er) {
            setErr(er instanceof ApiError ? `${er.status}: ${er.message}` : String(er));
          }
        }}
      >
        <label className={styles.label} htmlFor="pid">
          Program id
        </label>
        <input
          id="pid"
          className={styles.input}
          value={progId}
          onChange={(e) => setProgId(e.target.value)}
        />
        <label className={styles.label} htmlFor="assign">
          Assigned client userIds (comma-separated)
        </label>
        <input
          id="assign"
          className={styles.input}
          placeholder="cognito-sub-of-client"
          value={assignTo}
          onChange={(e) => setAssignTo(e.target.value)}
        />
        <button type="submit" className={styles.secondaryButton}>
          Save program assignment
        </button>
      </form>

      <button type="button" className={styles.linkButton} onClick={() => void load()}>
        Refresh roster
      </button>
      {err ? (
        <p role="alert" className={styles.error}>
          {err}
        </p>
      ) : null}
    </div>
  );
}

function MyPtHome(): JSX.Element {
  const [trainers, setTrainers] = useState<MemberRow[]>([]);
  useEffect(() => {
    void getClientsPt()
      .then((t) => setTrainers(t.trainers ?? []))
      .catch(() => setTrainers([]));
  }, []);
  if (!trainers.length) {
    return <p className={styles.muted}>No linked trainer yet.</p>;
  }
  return (
    <div className={styles.subCard}>
      <h2 className={styles.subTitle}>My PT</h2>
      <ul className={styles.rows}>
        {trainers.map((t) => (
          <li key={t.userId}>{t.email ?? t.userId}</li>
        ))}
      </ul>
    </div>
  );
}

function AdminPanel(): JSX.Element {
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [ptId, setPtId] = useState('');
  const [clientId, setClientId] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const loadMembers = async (): Promise<void> => {
    setErr(null);
    try {
      const m = await getMembers();
      setMembers(m.members ?? []);
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    }
  };

  useEffect(() => {
    void loadMembers();
  }, []);

  return (
    <div className={styles.subCard}>
      <h2 className={styles.subTitle}>Admin — PT ↔ client</h2>
      <p className={styles.muted}>
        Create <code className={styles.code}>MEMBERSHIP</code> rows via invite first, then link{' '}
        <code className={styles.code}>pt</code> + <code className={styles.code}>client</code> subs here.
      </p>
      <button type="button" className={styles.secondaryButton} onClick={() => void loadMembers()}>
        Refresh members
      </button>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>userId</th>
            <th>email</th>
            <th>role</th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.userId}>
              <td className={styles.value}>{m.userId}</td>
              <td>{m.email ?? '—'}</td>
              <td>{m.role}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <form
        className={styles.form}
        onSubmit={async (e) => {
          e.preventDefault();
          setMsg(null);
          setErr(null);
          try {
            await createPtLink(ptId.trim(), clientId.trim());
            setMsg('Link created.');
          } catch (er) {
            setErr(er instanceof ApiError ? `${er.status}: ${er.message}` : String(er));
          }
        }}
      >
        <label className={styles.label} htmlFor="ptid">
          PT userId (sub)
        </label>
        <input id="ptid" className={styles.input} value={ptId} onChange={(e) => setPtId(e.target.value)} />
        <label className={styles.label} htmlFor="clid">
          Client userId (sub)
        </label>
        <input
          id="clid"
          className={styles.input}
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
        />
        <button type="submit" className={styles.primaryButton}>
          POST /pt-links
        </button>
      </form>
      {msg ? <p className={styles.muted}>{msg}</p> : null}
      {err ? (
        <p role="alert" className={styles.error}>
          {err}
        </p>
      ) : null}
    </div>
  );
}

function SignedInShell(): JSX.Element {
  const { user, signOut } = useAuth();
  const [tab, setTab] = useState<Tab>('home');
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [meLoading, setMeLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setMeLoading(true);
    setError(null);
    getMe()
      .then((result) => {
        if (!cancelled) setMe(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? `API ${err.status}: ${err.message}` : String(err));
        }
      })
      .finally(() => {
        if (!cancelled) setMeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Optional: `?invite=<token>` after sign-in redeem flow */
  useEffect(() => {
    if (!me?.uid) return;
    const invite = new URLSearchParams(window.location.search).get('invite');
    if (!invite) return;
    let cancelled = false;
    void acceptInvite(invite)
      .then(() => !cancelled && window.history.replaceState({}, '', window.location.pathname))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [me?.uid]);

  const greeting = me
    ? `Hello, ${me.name || me.email || me.uid} (${me.role}@${me.tenantId})`
    : meLoading
      ? 'Loading your profile…'
      : 'Signed in.';

  return (
    <main className={styles.page}>
      <section className={`${styles.card} ${styles.wideCard}`}>
        <header className={styles.header}>
          <div>
            <h1 className={styles.title}>{greeting}</h1>
          </div>
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => {
              void signOut();
            }}
          >
            Sign out
          </button>
        </header>

        <Nav tab={tab} setTab={setTab} me={me} />

        {meLoading ? (
          <p className={styles.subtitle}>Fetching /me…</p>
        ) : error ? (
          <p role="alert" className={styles.error}>
            {error}
          </p>
        ) : me ? (
          <>
            {tab === 'home' ? (
              <>
                <dl className={styles.rows}>
                  <dt className={styles.label}>uid</dt>
                  <dd className={styles.value}>{me.uid}</dd>
                  <dt className={styles.label}>email</dt>
                  <dd className={styles.value}>{me.email || '—'}</dd>
                  <dt className={styles.label}>name</dt>
                  <dd className={styles.value}>{me.name || '—'}</dd>
                  <dt className={styles.label}>role</dt>
                  <dd className={styles.value}>{me.role}</dd>
                  <dt className={styles.label}>tenantId</dt>
                  <dd className={styles.value}>{me.tenantId}</dd>
                </dl>
                <TenantPickerStub tenantId={me.tenantId} role={me.role} />
                <BillingCard />
                {me.role === 'client' ? <MyPtHome /> : null}
              </>
            ) : null}
            {tab === 'pt' && user?.sub && me ? <PtPanel uid={user.sub} role={me.role} /> : null}
            {tab === 'admin' ? <AdminPanel /> : null}
          </>
        ) : null}

        <BuildInfoCard />

        {user?.sub ? (
          <p className={styles.footnote}>
            cognito sub: <code className={styles.code}>{user.sub}</code>
          </p>
        ) : null}
      </section>
    </main>
  );
}

function ShellRoutes(): JSX.Element {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <main className={styles.page}>
        <section className={styles.card}>
          <p className={styles.subtitle}>Loading…</p>
        </section>
      </main>
    );
  }
  return user ? <SignedInShell /> : <LoginScreen />;
}

export default function AppV1(): JSX.Element {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/admin" element={<AdminConsole />} />
          <Route path="/*" element={<ShellRoutes />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
